import React from "react";
import { useMemo } from "react";
import { Route, useLocation } from "react-router";
import { Redirect } from "react-router";
import { RouteComponentProps } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import { AuthRouteProps } from "../../types";
import DashboardLayout from "../../../app/components/layouts/DashboardLayout";
import { UserRole } from "../../../user/enums/UserRole";
import Error from "../../../../components/Error";
import { Routes } from "../../../../constants/routes";
import AccessControl from "../../../../components/AccessControl";

const AuthRoute = ({
  roles,
  verified = true,
  temporary = false,
  hideHeaderItems = false,
  profileVerified = true,
  mustHaveCredits,
  showCart = false,
  component: Component,
  ...rest
}: AuthRouteProps) => {
  const {
    user,
    profile: { data: { data: profile = null } = {} },
    data: { isAuthenticated },
  } = useAuth();
  const { pathname, search } = useLocation();

  const _isEmailVerified = useMemo(
    () => isAuthenticated && user.verified,
    [user, isAuthenticated],
  );
  const _hasCredits = useMemo(
    () => isAuthenticated && user.role === 4 && profile && profile.credits > 0,
    [user, profile, isAuthenticated],
  );
  const _isProfileVerified = useMemo(
    () => isAuthenticated && profile && profile.verified,
    [user, profile, isAuthenticated],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  function renderRoute(routeProps: RouteComponentProps) {
    let from = rest.location?.pathname || "/0";

    if (!isAuthenticated) {
      return (
        <Redirect
          to={`/${Routes.LOGIN}?from=${from + search.replace("?", "#")}`}
        />
      );
    }

    if (verified !== null && verified !== _isEmailVerified) {
      return _isEmailVerified ? (
        <Redirect to={"/0"} />
      ) : (
        <Redirect to={`/${Routes.VERIFY}`} />
      );
    }

    if (
      [UserRole.Agent, UserRole.School, UserRole.ServiceProvider].includes(
        user.role,
      ) &&
      profileVerified !== null &&
      profileVerified !== _isProfileVerified
    ) {
      return _isProfileVerified ? (
        <Redirect to={"/0"} />
      ) : (
        <Redirect to={`/${Routes.PROFILE_VERIFY}`} />
      );
    }

    if (
      user.role === UserRole.School &&
      _isEmailVerified &&
      _isProfileVerified
    ) {
      if (pathname.includes(Routes.VERIFY_SCHOOL) && profile.school) {
        return <Redirect to={"/0"} />;
      }

      if (!pathname.includes(Routes.VERIFY_SCHOOL) && !profile.school) {
        return <Redirect to={`/${Routes.VERIFY_SCHOOL}`} />;
      }
    }

    if (typeof mustHaveCredits === "boolean") {
      if (mustHaveCredits !== _hasCredits) {
        return _hasCredits ? (
          <Redirect to={"/0"} />
        ) : (
          <Redirect to={`/${Routes.SUBSCRIBE}`} />
        );
      }
    }

    return (
      <DashboardLayout
        showCart={showCart}
        hideHeaderItems={hideHeaderItems}
        {...routeProps}
      >
        <AccessControl
          allowedRoles={roles || []}
          renderNoAccess={() => {
            return <Error text={"You are not allowed to access this route."} />;
          }}
        >
          <Component {...routeProps} />
        </AccessControl>
      </DashboardLayout>
    );
  }

  return <Route render={renderRoute} {...rest} />;
};

export default React.memo(AuthRoute);
